<script>
import { mapGetters } from 'vuex';
import Banner from 'dashboard/components/ui/Banner.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';
import FranchisesAPI from 'dashboard/api/franchises';

// Warns a franchise user before the account locks them out, and explains the lockout
// once it happens. Administrators manage franchises rather than belong to one, so the
// endpoint returns nothing for them and the banner stays hidden.
export default {
  name: 'FranchiseStatusBanner',
  components: {
    Banner,
  },
  data() {
    return {
      franchise: null,
    };
  },
  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),
    isSuspended() {
      return this.franchise?.status === 'suspended';
    },
    isInGrace() {
      return this.franchise?.status === 'in_grace';
    },
    shouldShowBanner() {
      return this.isSuspended || this.isInGrace;
    },
    // An attendant is blocked by a suspended franchise but cannot open its pages, so
    // they get the explanation without an action button that would only 404.
    canOpenBilling() {
      return Boolean(this.franchise?.accessible);
    },
    bannerMessage() {
      if (!this.franchise) return '';
      if (!this.isSuspended) {
        return this.$t('FRANCHISES.BANNER.IN_GRACE', {
          name: this.franchise.name,
          days: this.franchise.grace_period_days,
        });
      }
      return this.suspendedMessage;
    },
    // O banner afirmava inadimplência para toda suspensão, inclusive a manual. O motivo já
    // vinha no payload de membership e ninguém o lia. O texto genérico continua sendo o
    // fallback: franquia cujo status foi gravado direto, antes desta mudança, não tem motivo.
    suspendedMessage() {
      const params = { name: this.franchise.name };
      const reason = this.franchise.suspension_reason;
      if (!reason) return this.$t('FRANCHISES.BANNER.SUSPENDED', params);

      const key = `FRANCHISES.BANNER.SUSPENDED_REASONS.${reason.toUpperCase()}`;
      const text = this.$t(key, params);
      return text === key
        ? this.$t('FRANCHISES.BANNER.SUSPENDED', params)
        : text;
    },
    billingPath() {
      return frontendURL(
        `accounts/${this.currentAccountId}/franchises/${this.franchise.id}/financial`
      );
    },
  },
  mounted() {
    this.fetchMembership();
  },
  methods: {
    async fetchMembership() {
      try {
        const { data } = await FranchisesAPI.myMembership();
        this.franchise = data?.franchise || null;
      } catch {
        // Non-critical: a user without franchise access simply gets no banner.
        this.franchise = null;
      }
    },
    routeToBilling() {
      this.$router.push(this.billingPath).catch(() => {});
    },
  },
};
</script>

<template>
  <div v-if="shouldShowBanner" class="franchise-status-banner shrink-0">
    <Banner
      :color-scheme="isSuspended ? 'alert' : 'warning'"
      :banner-message="bannerMessage"
      :action-button-label="
        canOpenBilling ? $t('FRANCHISES.BANNER.ACTION') : ''
      "
      :has-action-button="canOpenBilling"
      @click="routeToBilling"
    />
  </div>
</template>
